import React, { useState, useRef } from 'react'
import Modal from './MyModal'
import MyButton from './buttons'
import { FormControl } from '@mui/material'
import { BASE_URL, X_API_KEY } from '../../services/api'
import { useTranslation } from 'react-i18next'
import MyInput from './MyInput'
import { FaEnvelope } from 'react-icons/fa'

interface ForgotPasswordModalProps {
  isOpen: boolean
  onClose: () => void
  emailLabel?: string
  initialValue?: string
}

function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = ({
  isOpen,
  onClose,
  emailLabel,
  initialValue,
}) => {
  const { t } = useTranslation()
  const [email, setEmail] = useState(initialValue || '')
  const [successMessage, setSuccessMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [, setInvalidFields] = useState<Set<string>>(new Set())
  const emailInputRef = useRef<HTMLInputElement>(null)

  const handleEmailChange = (value: string) => {
    setEmail(value)
    setInvalidFields(new Set())
    if (!isValidEmail(value)) {
      setInvalidFields(new Set(['email']))
      setErrorMessage(t('validation-email-invalid'))
    } else {
      setErrorMessage('')
    }
  }

  const handleSubmit = async (e: { preventDefault: () => void }) => {
    e.preventDefault()
    setIsLoading(true)
    setSuccessMessage('')
    setErrorMessage('')
    setInvalidFields(new Set())

    try {
      if (!email.trim()) {
        setInvalidFields(new Set(['email']))
        setErrorMessage(t('validation-email-required'))
        if (emailInputRef.current) {
          emailInputRef.current.focus()
        }
        return
      }
      const response = await fetch(`${BASE_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': X_API_KEY || '',
        },
        body: JSON.stringify({ email }),
      })

      const data = await response.json()
      if (response.ok) {
        setSuccessMessage(t('if-account-with-email'))
        setTimeout(() => {
          onClose()
          setEmail('')
          setSuccessMessage('')
        }, 4000)
      } else {
        setErrorMessage(data.error || t('email-does-not-exist'))
      }
    } catch (error) {
      setErrorMessage('An unexpected error occurred. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className='bg-white rounded-lg w-full'>
        <h2 className='bg-anthracite text-white p-5 font-semibold text-xl text-center'>
          {t('password-modify')}
        </h2>

        <form onSubmit={handleSubmit} className='space-y-4 p-5'>
          <div className='space-y-2'>
            <span>{t('email-sent-to-verify')}</span>
            <FormControl fullWidth>
              <MyInput
                ref={emailInputRef}
                icon={<FaEnvelope />}
                className='mt-3'
                value={email}
                required
                onChange={handleEmailChange}
                label={emailLabel || ''}
              />
            </FormControl>
          </div>

          {successMessage && (
            <div className='bg-greenlight text-green-700 p-2 text-sm rounded mb-4'>
              {successMessage}
            </div>
          )}

          {errorMessage && (
            <div className='bg-redlight text-anthracite p-2 text-sm rounded mb-4'>
              {errorMessage}
            </div>
          )}

          <MyButton
            variant='redpink-anthracite'
            type='submit'
            className='w-full'
            disabled={isLoading}
          >
            {isLoading ? 'Sending...' : `${t('confirm')}`}
          </MyButton>
        </form>
      </div>
    </Modal>
  )
}

export default ForgotPasswordModal
